import { useQuery } from "@apollo/client/react";
import { Box, Flex, Text } from "@nulogy/components";
import HalfField from "components/common/HalfField";
import Section from "components/common/Section";
import DatePicker from "components/controls/DatePicker";
import Form from "components/controls/Form";
import LockToggle from "components/controls/LockToggle";
import Select from "components/controls/Select";
import { formatDuration } from "components/helpers/format";
import DowntimeReasonSelect from "components/pages/schedule/downtimeBlocks/DowntimeReasonSelect";
import DowntimeBlock from "domain/DowntimeBlock";
import { GetDowntimeReasons } from "hooks/apollo/downtimeReason/downtimeReason.gql";
import React from "react";
import DateTime from "utils/DateTime";

export default function CreateDowntimeBlockForm(props) {
  const {
    onClose,
    onSave,
    lines,
    setErrors,
    blockData: { block, startDate, endDate },
  } = props;
  const { data: downtimeReasonsData, loading: downtimeReasonsLoading } = useQuery(GetDowntimeReasons);

  if (downtimeReasonsLoading) return null;

  const lineOptions = lines.map((line) => ({ label: line.name, value: line.id }));

  const initialValues = {
    ...DowntimeBlock.build({
      endsAt: DateTime.toISO8601(endDate),
      line: block.line,
      reason: "",
      startsAt: DateTime.toISO8601(startDate),
    }),
  };

  let { overlappingPlannedBreaks } = initialValues;

  const validate = (data) => {
    const errors = DowntimeBlock.validate(data, downtimeReasonsData.downtimeReasons.length);

    setErrors(errors);

    return errors;
  };

  const handleSave = (data) =>
    new Promise(() => {
      onSave({ ...data, __typename: "DowntimeBlock" });
      onClose();
    });

  const handleFormChange = (e) => {
    const downtimeBlock = e.values;

    if (downtimeBlock) {
      const newLine = lines.find((line) => line.id === downtimeBlock.line.id);
      overlappingPlannedBreaks = DowntimeBlock.updateOverlappingPlannedBreaks({
        ...downtimeBlock,
        line: newLine,
      }).overlappingPlannedBreaks;
    }
  };

  return (
    <Form
      enableReinitialize
      id="createDowntime"
      initialValues={initialValues}
      onChange={handleFormChange}
      onSave={handleSave}
      validate={validate}
    >
      {({ values: { lockedOnSchedule } }) => (
        <Section controls={<LockToggle field="lockedOnSchedule" />} mb={0} title="Scheduling details">
          <DowntimeReasonSelect
            downtimeReasonsData={downtimeReasonsData}
            downtimeReasonsLoading={downtimeReasonsLoading}
            lockedOnSchedule={lockedOnSchedule}
          />
          <Box marginBottom="x2">
            <Select
              className="spec-line-select"
              disabled={lockedOnSchedule}
              field="line[id]"
              labelText="Line"
              maxHeight="250"
              options={lineOptions}
            />
          </Box>
          <Box marginBottom="x2">
            <Flex className="customDatePicker">
              <Box marginRight="x3" width={1 / 2}>
                <DatePicker className="spec-startsAt" disabled={lockedOnSchedule} field="startsAt" labelText="Starts" />
              </Box>
              <Box marginLeft="x3" width={1 / 2}>
                <DatePicker className="spec-endsAt" disabled={lockedOnSchedule} field="endsAt" labelText="Ends" />
              </Box>
            </Flex>
          </Box>
          {overlappingPlannedBreaks.length > 0 && (
            <HalfField labelText="Planned breaks" mb="0px">
              {overlappingPlannedBreaks.map(({ plannedBreakName, duration }) => (
                <Text key={plannedBreakName}>{`${plannedBreakName}: ${formatDuration(duration)}`}</Text>
              ))}
            </HalfField>
          )}
        </Section>
      )}
    </Form>
  );
}
